import {
  addDays,
  addMonths,
  addYears,
  format,
  getDay,
  getYear,
  isSameDay,
  isSameMonth,
  isSameYear,
  isAfter,
  isValid,
  parse,
  setHours,
  setMinutes,
  setMonth,
  setYear,
  startOfMonth,
  startOfYear,
  subDays,
} from 'date-fns';

export const DATE_PICKER_MODE = {
  DATE: 'date',
  DATETIME: 'datetime',
  MONTH: 'month',
} as const;

export type CustomDatePickerMode = (typeof DATE_PICKER_MODE)[keyof typeof DATE_PICKER_MODE];

export const CALENDAR_PANEL = {
  DAYS: 'days',
  MONTHS: 'months',
  YEARS: 'years',
} as const;

export type CalendarPanel = (typeof CALENDAR_PANEL)[keyof typeof CALENDAR_PANEL];

export const previousStep = -1 as const;
export const nextStep = 1 as const;
export type CalendarViewStep = typeof previousStep | typeof nextStep;

/** Number of years shown / shifted in the years panel. */
export const YEAR_WINDOW_SIZE = 12;

export const DATE_DISPLAY_FORMAT = 'dd/MM/yyyy';
export const DATETIME_DISPLAY_FORMAT = 'dd/MM/yyyy HH:mm';
export const MONTH_DISPLAY_FORMAT = 'MMMM yyyy';

export const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;
export const MONTH_SHORT_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const MONTH_PARSE_FORMATS = ['MMMM yyyy', 'MMM yyyy', 'MM/yyyy', 'M/yyyy', 'yyyy-MM'] as const;

export type DatePickerRestrictions = {
  userRelationDays?: number | null;
  userRelationVacationDays?: string | null;
};

export const isAfterMaxDate = (value: Date, maxDate?: Date) =>
  maxDate !== undefined && isAfter(value, maxDate);

const dateOnly = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/** Matches the Legacy grid: vacation values are day numbers in the current month. */
export const getDatePickerRestrictions = (restrictions?: DatePickerRestrictions) => {
  const relationDays = Number(restrictions?.userRelationDays ?? 0);
  if (!(relationDays > 0)) return null;

  const today = dateOnly(new Date());
  const vacationDates = new Set(
    (restrictions?.userRelationVacationDays || '')
      .split(',')
      .map((day) => new Date(today.getFullYear(), today.getMonth(), parseInt(day, 10)))
      .filter((day) => !isNaN(day.getTime()))
      .map((day) => dateOnly(day).getTime())
  );
  const isValidWorkDay = (date: Date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !vacationDates.has(dateOnly(date).getTime());
  };

  const minDate = new Date(today);
  let count = isValidWorkDay(minDate) ? 1 : 0;
  while (count < relationDays) {
    minDate.setDate(minDate.getDate() - 1);
    if (isValidWorkDay(minDate)) count++;
  }

  return {
    isDisabled: (date: Date) =>
      dateOnly(date) < dateOnly(minDate) || vacationDates.has(dateOnly(date).getTime()),
  };
};

export const padTimePart = (value: number) => String(value).padStart(2, '0');

export const clampTimePart = (raw: string, max: number): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed > max) {
    return null;
  }

  return parsed;
};

export const applyTimeToDate = (date: Date, hours: number, minutes: number): Date =>
  setMinutes(setHours(date, hours), minutes);

export const formatPickerValue = (value: Date | null, mode: CustomDatePickerMode): string => {
  if (!value || !isValid(value)) {
    return '';
  }

  if (mode === DATE_PICKER_MODE.DATETIME) {
    return format(value, DATETIME_DISPLAY_FORMAT);
  }
  if (mode === DATE_PICKER_MODE.MONTH) {
    return format(value, MONTH_DISPLAY_FORMAT);
  }
  return format(value, DATE_DISPLAY_FORMAT);
};

/** Formats a calendar date using the local timezone, without UTC date shifting. */
export const formatLocalDate = (value: Date): string => format(value, 'yyyy-MM-dd');

/** Returns the first and last calendar dates of the current local month. */
export const getCurrentLocalMonthDateRange = (now = new Date()) => ({
  from: formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 1)),
  to: formatLocalDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
});

export const parsePickerInput = (text: string, mode: CustomDatePickerMode): Date | null => {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }

  if (mode === DATE_PICKER_MODE.MONTH) {
    for (const parseFormat of MONTH_PARSE_FORMATS) {
      const parsed = parse(trimmed, parseFormat, new Date());
      if (isValid(parsed)) {
        return startOfMonth(parsed);
      }
    }
    return null;
  }

  const parseFormat =
    mode === DATE_PICKER_MODE.DATETIME ? DATETIME_DISPLAY_FORMAT : DATE_DISPLAY_FORMAT;
  const parsed = parse(trimmed, parseFormat, new Date());

  if (!isValid(parsed)) {
    return null;
  }

  if (mode === DATE_PICKER_MODE.DATE) {
    parsed.setHours(0, 0, 0, 0);
  }

  return parsed;
};

/** Six-week grid (42 days) starting Monday. */
export const buildCalendarDays = (viewMonth: Date): Date[] => {
  const monthStart = startOfMonth(viewMonth);
  const mondayOffset = (getDay(monthStart) + 6) % 7;
  const gridStart = subDays(monthStart, mondayOffset);

  return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
};

/** 12 month starts for the year of `viewMonth`. */
export const buildYearMonths = (viewMonth: Date): Date[] => {
  const yearStart = startOfYear(viewMonth);
  return Array.from({ length: 12 }, (_, index) => setMonth(yearStart, index));
};

/** Year window for the years panel (e.g. 2020–2031). */
export const buildYearWindow = (viewMonth: Date): Date[] => {
  const year = getYear(viewMonth);
  const windowStart = Math.floor(year / YEAR_WINDOW_SIZE) * YEAR_WINDOW_SIZE;
  return Array.from({ length: YEAR_WINDOW_SIZE }, (_, index) =>
    setYear(startOfYear(viewMonth), windowStart + index)
  );
};

export const getYearWindowLabel = (viewMonth: Date): string => {
  const years = buildYearWindow(viewMonth);
  return `${getYear(years[0])} – ${getYear(years[years.length - 1])}`;
};

/** Shift calendar view by one step for the active panel. */
export const shiftCalendarView = (
  viewMonth: Date,
  panel: CalendarPanel,
  step: CalendarViewStep
): Date => {
  if (panel === CALENDAR_PANEL.DAYS) {
    return addMonths(viewMonth, step);
  }
  if (panel === CALENDAR_PANEL.MONTHS) {
    return addYears(viewMonth, step);
  }
  return addYears(viewMonth, step * YEAR_WINDOW_SIZE);
};

export const isCalendarDayOutsideMonth = (day: Date, viewMonth: Date) =>
  !isSameMonth(day, viewMonth);

export const getInitialCalendarPanel = (mode: CustomDatePickerMode): CalendarPanel =>
  mode === DATE_PICKER_MODE.MONTH ? CALENDAR_PANEL.MONTHS : CALENDAR_PANEL.DAYS;

/** Ensures only one CustomDatePicker popover is open at a time. */
type DatePickerOpenListener = (openerId: string) => void;
const datePickerOpenListeners = new Set<DatePickerOpenListener>();

export const notifyCustomDatePickerOpened = (openerId: string) => {
  datePickerOpenListeners.forEach((listener) => listener(openerId));
};

export const subscribeCustomDatePickerOpened = (listener: DatePickerOpenListener) => {
  datePickerOpenListeners.add(listener);
  return () => {
    datePickerOpenListeners.delete(listener);
  };
};

export { getYear, isSameDay, isSameMonth, isSameYear, isValid, setYear, startOfMonth };
